import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Sheet from './Sheet';
import { MealSheet, WorkoutSheet } from './CaptureSheets';
import Icon from './Icon';
import { today } from '../lib/dates';
import { useApp, useAppPath } from '../lib/AppContext';

// Every log goes: capture button → choose type → a bottom sheet → AI analysis → review → save.
// The shell owns the sheets so the raised camera button works on every screen; pages that show a day
// subscribe to the refreshed day the API returns after a save.
const CaptureContext = createContext(null);
export const useCapture = () => useContext(CaptureContext);

// Keeps `setDay` fed with saves for `date`; the day page also tells the shell which date it shows,
// so the camera button logs to that day.
export function useCapturedDay(date, setDay, { active = false } = {}) {
  const { subscribe, setActiveDate } = useCapture();
  useEffect(() => subscribe((day) => {
    if (day.date !== date) return false;
    setDay(day);
    return true;
  }), [subscribe, date, setDay]);
  useEffect(() => {
    if (!active) return undefined;
    setActiveDate(date);
    return () => setActiveDate(null);
  }, [active, date, setActiveDate]);
}

function ChooserRow({ icon, tile, title, text, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="flex min-h-11 w-full items-center gap-4 rounded-[20px] border border-line bg-base-100 p-4 text-left transition active:scale-[0.99]">
      <span className={`grid size-14 shrink-0 place-items-center rounded-2xl ${tile}`}><Icon name={icon} className="size-7" /></span>
      <span className="flex-1">
        <span className="block text-[15px] font-semibold">{title}</span>
        <span className="block text-sm text-muted">{text}</span>
      </span>
      <Icon name="chevronRight" className="size-5 text-muted" />
    </button>
  );
}

export function CaptureProvider({ children }) {
  const { editable } = useApp();
  const navigate = useNavigate();
  const path = useAppPath();
  const [sheet, setSheet] = useState(null);
  const activeDate = useRef(null);
  const listeners = useRef(new Set());

  const open = useCallback((kind = 'choose', options = {}) => {
    setSheet({ kind, ...options, date: options.date || activeDate.current || today() });
  }, []);
  const subscribe = useCallback((listener) => {
    listeners.current.add(listener);
    return () => listeners.current.delete(listener);
  }, []);
  const setActiveDate = useCallback((date) => { activeDate.current = date; }, []);

  // Pages showing the saved day update in place; anywhere else, go to that day to show the result.
  const saved = useCallback((day) => {
    const handled = [...listeners.current].map((listener) => listener(day)).some(Boolean);
    if (!handled) navigate(path(day.date === today() ? '/day' : `/day?date=${day.date}`));
  }, [navigate, path]);

  const value = useMemo(() => ({ open, subscribe, setActiveDate, saved, enabled: editable }),
    [open, subscribe, setActiveDate, saved, editable]);
  const close = () => setSheet(null);
  const done = (day) => { close(); saved(day); };

  return (
    <CaptureContext.Provider value={value}>
      {children}
      {editable && (
        <>
          <Sheet open={sheet?.kind === 'choose'} onClose={close} title="Bạn muốn ghi gì?">
            <div className="flex flex-col gap-3">
              <ChooserRow icon="bowl" tile="bg-[#e3f1e9] text-[#17804f]" title="Bữa ăn"
                text="Chụp hoặc mô tả món ăn, AI tính calo và đạm" onClick={() => open('meal', { date: sheet.date })} />
              <ChooserRow icon="fire" tile="bg-train-bg text-train" title="Tập luyện"
                text="Ghi tên bài, thời lượng, kcal · nhiều môn một lần" onClick={() => open('workout', { date: sheet.date })} />
            </div>
          </Sheet>
          <MealSheet sheet={sheet?.kind === 'meal' ? sheet : null} onClose={close} onSaved={done} />
          <WorkoutSheet sheet={sheet?.kind === 'workout' ? sheet : null} onClose={close} onSaved={done} />
        </>
      )}
    </CaptureContext.Provider>
  );
}
